import {
    unsubscribeDialogTitleText,
    unsubscribeDialogSubTitleText,
} from './unsubscribeEmail.locstring.json';
import { unsubscribe as unsubscribe_1 } from 'owa-locstrings/lib/strings/unsubscribe.locstring.json';
import { cancelButton } from 'owa-locstrings/lib/strings/cancelbutton.locstring.json';
import loc, { format } from 'owa-localize';

import datapoints from './datapoints';
import deleteItemsStoreUpdate, {
    ItemContext,
} from 'owa-mail-actions/lib/triage/deleteItemsStoreUpdate';
import getItem from 'owa-mail-store/lib/services/getItem';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import itemResponseShape from 'owa-service/lib/factory/itemResponseShape';
import Message from 'owa-service/lib/contract/Message';
import MessageSafetyLevel from 'owa-service/lib/contract/MessageSafetyLevel';
import reportMessageToSafetyLibraryService from '../services/reportMessageToSafetyLibraryService';
import SafetyApiAction from 'owa-service/lib/contract/SafetyApiAction';
import store from 'owa-mail-store/lib/store/Store';

import { action, select } from 'satcheljs/lib/legacy';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { ActionSource, ClientItem } from 'owa-mail-store';
import { confirm, ConfirmCustomizationOptions, DialogResponse } from 'owa-confirm-dialog';

import { listViewStore, TableView } from 'owa-mail-list-store';
import { ServiceFetchError } from 'owa-service/lib/ServiceFetchError';

export interface UnsubscribeEmailState {
    message: Message;
    originalTableView: TableView;
    reportToExternalSender: boolean;
}

const UnsubscribeEmailActionName = 'unsubscribeEmail';

// The regular expression of message header of a trusted sender
const TRUSTED_MESSAGE_HEAD_REGX = new RegExp('List-Unsubscribe:[ ]+<mailto:');

/**
 * Perform the unsubscribe action without the confirmation dialog
 * @param message the message
 * @param originalTableView the table view that message belongs to
 * @param isTrustedUnsubscribe whether to set actionData for trusted unsubscribe
 * @param actionSource the user source that initated the action
 */
const unsubscribeWithoutConfirmDialog = createLegacyActionWithDatapoint(
    'unsubscribeWithoutConfirmDialog',
    datapoints.unsubscribe
)(function unsubscribeWithoutConfirmDialog(
    message: Message,
    originalTableView: TableView,
    isTrustedUnsubscribe: boolean,
    actionSource: ActionSource
) {
    const itemContext: ItemContext = {
        itemId: message.ItemId.Id,
        itemConversationId: message.ConversationId,
        isRead: message.IsRead,
        mailboxInfo: (message as ClientItem).MailboxInfo,
    };

    deleteItemsStoreUpdate([itemContext], message.ParentFolderId.Id);

    // TODO: VSO 11909: Handle undo for destructive mail actions
    const reportMessageToSafetyLibraryPromise = reportMessageToSafetyLibraryService(
        [message.ItemId.Id],
        SafetyApiAction.Unsubscribe,
        isTrustedUnsubscribe ? [1] : undefined
    );

    return reportMessageToSafetyLibraryPromise
        .then(response => {
            if (!response) {
                const error = new Error(
                    'Failed to report message to safety library!'
                ) as ServiceFetchError;
                error.fetchErrorType = 'ServerFailure';
                return Promise.reject(error);
            }

            return Promise.resolve();
        })
        .catch(error => {
            return Promise.reject(error);
        });
});

/**
 * Show the unsubscribe confirmation dialog
 * @param message the message
 * @param originalTableView the table view
 * @param isTrustedUnsubscribe whether to perform the trusted unsubscribe
 * @param actionSource the user source that initated the action
 * @return a Promise when the action completes
 */
const showUnsubscribeDialog = action('showUnsubscribeDialog')(function showUnsubscribeDialog(
    message: Message,
    originalTableView: TableView,
    isTrustedUnsubscribe: boolean,
    actionSource: ActionSource
): Promise<void> {
    return confirm(
        loc(unsubscribeDialogTitleText),
        format(loc(unsubscribeDialogSubTitleText), message.From.Mailbox.Name),
        false /* resolevImmediately */,
        <ConfirmCustomizationOptions>{
            okText: loc(unsubscribe_1),
            cancelText: loc(cancelButton),
        }
    ).then(async (response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            return unsubscribeWithoutConfirmDialog(
                message,
                originalTableView,
                isTrustedUnsubscribe,
                actionSource
            );
        }
    });
});

/**
 * Perform the unsubscribe action with the confirmation dialog
 * @param itemId the item id
 * @param tableViewId the table view id
 * @param actionSource the user source that initated the action
 * @param state the state which contains the tableView and reportToExternalSender in user configuration
 */
export default select<UnsubscribeEmailState>({
    message: (itemId: string, tableViewId: string) => store.items.get(itemId),
    originalTableView: (itemId: string, tableViewId: string) =>
        listViewStore.tableViews.get(tableViewId),
    reportToExternalSender: () => getUserConfiguration().SafetyUserOptions.ReportToExternalSender,
})(
    action(UnsubscribeEmailActionName)(function unsubscribeEmail(
        itemId: string,
        tableViewId: string,
        actionSource: ActionSource,
        state?: UnsubscribeEmailState
    ) {
        const { message, originalTableView, reportToExternalSender } = state;

        const isMessageLevelSafeOrTrusted =
            message.MessageSafety &&
            (message.MessageSafety.MessageSafetyLevel == MessageSafetyLevel.Safe ||
                message.MessageSafety.MessageSafetyLevel == MessageSafetyLevel.Trusted);

        if (!reportToExternalSender || !isMessageLevelSafeOrTrusted) {
            // Show confirmation dialog for untrusted sender unsubscribe if reportToExternalSender is not enabled,
            // or the MessageSafetyLevel isn't Safe or Trusted
            return showUnsubscribeDialog(
                message,
                originalTableView,
                false /* isTrustedUnsubscribe */,
                actionSource
            );
        }

        const itemShape = itemResponseShape({
            BaseShape: 'IdOnly',
        });

        // We need to get the headers for this message to test
        // for the List-Unsubscribe before performing a trusted unsubscribe
        return getItem([message.ItemId.Id], itemShape, 'MessageDetails').then(response => {
            if (response[0].ResponseClass == 'Success') {
                const responseItem = response[0] as ItemInfoResponseMessage;
                const messageHeader =
                    responseItem.Items[0].ExtendedProperty &&
                    responseItem.Items[0].ExtendedProperty[0].Value;

                return showUnsubscribeDialog(
                    message,
                    originalTableView,
                    TRUSTED_MESSAGE_HEAD_REGX.test(messageHeader) /* isTrustedUnsubscribe */,
                    actionSource
                );
            } else {
                throw new Error(response[0].MessageText);
            }
        });
    })
);
